import { useAuth } from '@/hooks/useAuth';
import { useUsageCounts } from '@/hooks/useUsageCounts';
import { UNLIMITED } from '@/lib/entitlements';
import type { PlanTier } from '@/lib/billing';
import { Progress } from '@/components/ui/progress';

interface UsageMeterProps {
  planTier: PlanTier | null;
  planSubTier?: string | null;
  isSchoolAdmin?: boolean;
}

function UsageRow({ label, current, max }: { label: string; current: number; max: number }) {
  const pct = Math.min(100, Math.round((current / max) * 100));
  const atLimit = pct >= 100;
  const nearLimit = pct >= 80;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className={atLimit ? 'text-destructive font-medium' : nearLimit ? 'text-amber-600 font-medium' : 'text-foreground'}>
          {current} <span className="text-muted-foreground">/ {max}</span>
        </span>
      </div>
      <Progress
        value={pct}
        className={atLimit ? '[&>div]:bg-destructive' : nearLimit ? '[&>div]:bg-amber-500' : '[&>div]:bg-primary'}
      />
    </div>
  );
}

export default function UsageMeter({ planTier, planSubTier, isSchoolAdmin = false }: UsageMeterProps) {
  const { user, profile } = useAuth();
  const { assessmentsThisMonth, maxAssessments, pupils, maxPupils, classes, maxClasses, loading } =
    useUsageCounts({
      userId: user?.id,
      schoolId: profile?.school_id ?? undefined,
      planTier,
      planSubTier: planSubTier ?? null,
      isSchoolAdmin,
    });

  if (loading) return null;
  if (maxAssessments === UNLIMITED && maxPupils === UNLIMITED && maxClasses === UNLIMITED) return null;

  return (
    <div className="space-y-3">
      {maxAssessments !== UNLIMITED && (
        <UsageRow label="Assessments this month" current={assessmentsThisMonth} max={maxAssessments} />
      )}
      {!isSchoolAdmin && maxPupils !== UNLIMITED && (
        <UsageRow label="Pupils" current={pupils} max={maxPupils} />
      )}
      {!isSchoolAdmin && maxClasses !== UNLIMITED && (
        <UsageRow label="Classes" current={classes} max={maxClasses} />
      )}
    </div>
  );
}
